// react and js
import { useEffect, useCallback } from "react";

// mui

// components

// hooks

// layouts

// pages

// theme

// utils

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
// Wraps the reCAPTCHA v3 JS API. Loading the grecaptcha script and the
// promise that resolves once `window.grecaptcha.execute` is callable
// are deliberately module-scoped — both stay alive across remounts and
// across all auth widgets so multiple consumers share one script load.
const SCRIPT_ID = "recaptcha-v3-script";
let scriptLoadPromise = null;

function loadGrecaptchaScript(siteKey) {
  if (typeof window === "undefined") return Promise.resolve(null);

  if (scriptLoadPromise) return scriptLoadPromise;

  if (window.grecaptcha && window.grecaptcha.execute) {
    scriptLoadPromise = new Promise((resolve) => {
      window.grecaptcha.ready(() => resolve(window.grecaptcha));
    });
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const onReady = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => resolve(window.grecaptcha));
      } else {
        reject(new Error("grecaptcha did not initialise"));
      }
    };

    if (existing) {
      existing.addEventListener("load", onReady);
      existing.addEventListener("error", () =>
        reject(new Error("Failed to load grecaptcha script"))
      );
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = onReady;
    script.onerror = () =>
      reject(new Error("Failed to load grecaptcha script"));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
// Pre-loads grecaptcha v3 on mount (cheap and idempotent across mounts)
// and returns an executor. v3 tokens are single-use server-side and
// expire after ~2 minutes — callers must fetch a fresh token *per submit*
// with the action string the backend expects (login / create_account /
// password_reset / ...). The siteKey is read from
// REACT_APP_RECAPTCHA_V3_SITEKEY and must match the secret used by
// VerifyRecaptchaV3 in the API repo.
export default function useRecaptchaV3() {
  const siteKey = process.env.REACT_APP_RECAPTCHA_V3_SITEKEY;

  useEffect(() => {
    if (!siteKey) return;
    loadGrecaptchaScript(siteKey).catch(() => {});
  }, [siteKey]);

  const executeRecaptchaV3 = useCallback(
    async (action) => {
      if (!siteKey) return "";
      try {
        const grecaptcha = await loadGrecaptchaScript(siteKey);
        if (!grecaptcha) return "";
        return await grecaptcha.execute(siteKey, { action });
      } catch (e) {
        return "";
      }
    },
    [siteKey]
  );

  return executeRecaptchaV3;
}
